import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  CreditCard,
  Building2,
  Loader2,
  Handshake,
  ShieldCheck,
  AlertCircle,
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import OrderSummary from "../components/checkout/OrderSummary";
import CountrySelect, {
  CountryItem,
} from "../components/checkout/CountrySelect";
import CitySelect from "../components/checkout/CitySelect";
import ProvinceSelect, {
  ITALIAN_PROVINCES,
} from "../components/checkout/ProvinceSelect";
import {
  availableGateways,
  confirmToStructureGateway,
} from "../lib/payment-gateways";
import { environment } from "../config/env";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { PaymentMethodType } from "../types/payment";
import {
  PrivacyPolicyDialog,
  useStructurePrivacy,
} from "../components/PrivacyPolicyDialog";

interface Variant {
  id: number;
  name: string;
  checkIn?: string;
  checkOut?: string;
  option?: boolean;
  depositValue?: number;
  depositType?: string;
  services?: Array<{
    id?: number;
    serviceCode?: string;
    description: string;
    amount: number;
    isOneTime?: boolean;
    quantity?: number;
  }>;
  paymentCodes?: string[];
  paymentMethods?: PreventivePaymentMethod[];
  room?: Array<{
    description?: string;
    code?: string;
    guestNumber?: number;
    rate?: Array<{
      board?: Array<{
        riep?: { totale?: number };
      }>;
    }>;
  }>;
}

const formatDepositDescription = (value?: number, type?: string): string => {
  if (value == null) return "";
  const t = (type || "").trim().toLowerCase();
  if (t === "percent") return `${value}%`;
  if (t === "nights" || t === "night")
    return `${value} ${value === 1 ? "notte" : "notti"}`;
  return `${value} €`;
};

interface PreventivePaymentMethod {
  id: number;
  descrizione?: string;
  depositValue?: number;
  type?: number;
  isActive: boolean;
  requiresDeposit: boolean;
  isChannelManager: boolean;
  depositType?: string;
}

interface SysdatPayment {
  code: string;
  method?: string;
  description?: string;
  count?: string;
  value?: number;
  servicesInc?: string;
  localPaymentMethodId?: number;
  localPaymentMethodName?: string;
}

// Default fallback countries in case of network issue
const FALLBACK_COUNTRIES: CountryItem[] = [
  { id: 1, name: "Italia", iso2: "IT" },
  { id: 2, name: "Germania", iso2: "DE" },
  { id: 3, name: "Francia", iso2: "FR" },
  { id: 4, name: "Regno Unito", iso2: "GB" },
  { id: 5, name: "Spagna", iso2: "ES" },
  { id: 6, name: "Svizzera", iso2: "CH" },
  { id: 7, name: "Austria", iso2: "AT" },
  { id: 8, name: "Stati Uniti", iso2: "US" },
  { id: 9, name: "Paesi Bassi", iso2: "NL" },
  { id: 10, name: "Belgio", iso2: "BE" },
];

function parseDateString(s?: string | null): Date | null {
  if (!s) return null;
  const datePart = s.includes("T") ? s.split("T")[0] : s.split(" ")[0];
  const parts = datePart.split("-");
  if (parts.length === 3) {
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
      return new Date(y, m, d);
    }
  }
  const parsed = new Date(s);
  return isNaN(parsed.getTime()) ? null : parsed;
}

export default function CheckoutPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("checkout");

  const locationState = location.state as
    | {
        variant?: Variant;
        adultsNumber?: number;
        childrenNumber?: number;
      }
    | undefined;

  const locationVariant = locationState?.variant;
  const [selectedVariant, setSelectedVariant] = useState<Variant | undefined>(
    locationVariant,
  );
  const [adultsNumber, setAdultsNumber] = useState<number | undefined>(
    locationState?.adultsNumber,
  );
  const [childrenNumber, setChildrenNumber] = useState<number | undefined>(
    locationState?.childrenNumber,
  );
  const variant = selectedVariant;

  const isArrivalPassed = useMemo(() => {
    if (!variant?.checkIn) return false;
    const d = parseDateString(variant.checkIn);
    if (!d) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
  }, [variant?.checkIn]);

  // Form fields
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [cap, setCap] = useState("");
  const [country, setCountry] = useState("");
  const [notes, setNotes] = useState("");

  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Countries from GeoController
  const [countries, setCountries] = useState<CountryItem[]>(FALLBACK_COUNTRIES);
  const [loadingCountries, setLoadingCountries] = useState(false);

  // Payment methods
  const [allowedMethods, setAllowedMethods] = useState<
    PreventivePaymentMethod[]
  >([]);
  const [sysdatMethods, setSysdatMethods] = useState<SysdatPayment[]>([]);
  const [selectedMethodKey, setSelectedMethodKey] = useState<string>("crm-1");
  const [estimatePaymentMethodId, setEstimatePaymentMethodId] = useState<
    number | undefined
  >();
  const [estimateDepositValue, setEstimateDepositValue] = useState<
    number | undefined
  >();
  const [estimateDepositType, setEstimateDepositType] = useState<
    string | undefined
  >();
  const [paymentBankTransferText, setPaymentBankTransferText] = useState<
    string | null
  >(null);
  const [gatewayReady, setGatewayReady] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [structureId, setStructureId] = useState<number | null>(null);
  const [isPrivacyDialogOpen, setIsPrivacyDialogOpen] = useState(false);
  const { privacyPolicyHtml } = useStructurePrivacy(structureId);

  // Focus ref for first error
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Load countries from GeoController
  useEffect(() => {
    axios.defaults.baseURL = environment.ELITE_BASE_URI;
    setLoadingCountries(true);
    axios
      .get("/Geo/countries")
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          const list: CountryItem[] = res.data.map((c: any) => ({
            id: c.id,
            name: c.name,
            iso2: (c.iso2 || "").toUpperCase(),
          }));
          setCountries(list);
        }
      })
      .catch(() => {
        // Keeps fallback countries
      })
      .finally(() => {
        setLoadingCountries(false);
      });
  }, []);

  // Load estimate details
  useEffect(() => {
    axios.defaults.baseURL = environment.ELITE_BASE_URI;
    if (token) {
      setLoadingPayments(true);
      axios
        .get(`/Estimate/preventive-detail/${token}`)
        .then((res) => {
          if (res.data) {
            const resolvedStructId =
              res.data.structureId || res.data.minisite?.idStructure;
            if (resolvedStructId) {
              setStructureId(Number(resolvedStructId));
            }
            if (res.data.name) setName((prev) => prev || res.data.name);
            if (res.data.surname)
              setSurname((prev) => prev || res.data.surname);
            if (res.data.email) setEmail((prev) => prev || res.data.email);
            if (res.data.phone) setPhone((prev) => prev || res.data.phone);

            if (
              res.data.adultsNumber !== undefined &&
              res.data.adultsNumber !== null
            ) {
              setAdultsNumber(res.data.adultsNumber);
            }
            if (
              res.data.childrenNumber !== undefined &&
              res.data.childrenNumber !== null
            ) {
              setChildrenNumber(res.data.childrenNumber);
            }

            if (res.data.minisite?.paymentBankTransferText) {
              setPaymentBankTransferText(
                res.data.minisite.paymentBankTransferText,
              );
            }

            if (
              res.data.idPaymentMethod !== undefined &&
              res.data.idPaymentMethod !== null
            ) {
              setEstimatePaymentMethodId(res.data.idPaymentMethod);
            }
            if (
              res.data.depositValue !== undefined &&
              res.data.depositValue !== null
            ) {
              setEstimateDepositValue(res.data.depositValue);
            }
            if (res.data.depositType) {
              setEstimateDepositType(res.data.depositType);
            }

            if (res.data.allowedPaymentMethods) {
              setAllowedMethods(res.data.allowedPaymentMethods);
            }
            if (res.data.sysdatPaymentMethods) {
              setSysdatMethods(res.data.sysdatPaymentMethods);
            }

            const serverVariants: Variant[] =
              res.data.estimateVariants || res.data.variants || [];
            if (locationVariant) {
              const matched = serverVariants.find(
                (v) => v.id === locationVariant.id,
              );
              if (matched) {
                setSelectedVariant({
                  ...locationVariant,
                  ...matched,
                  services:
                    matched.services && matched.services.length > 0
                      ? matched.services
                      : locationVariant.services,
                  paymentMethods:
                    matched.paymentMethods && matched.paymentMethods.length > 0
                      ? matched.paymentMethods
                      : locationVariant.paymentMethods,
                });
              }
            } else if (serverVariants.length > 0) {
              const defaultV =
                serverVariants.find((v) => v.option) || serverVariants[0];
              setSelectedVariant(defaultV);
            }
          }
        })
        .catch(() => {})
        .finally(() => {
          setLoadingPayments(false);
        });
    }
  }, [token]);

  // Find currently selected country object
  const currentCountryObj = useMemo(() => {
    return countries.find(
      (c) => c.iso2.toLowerCase() === country.toLowerCase(),
    );
  }, [countries, country]);

  // Validation logic
  const validateField = (field: string, val: string): string => {
    const trimmed = (val || "").trim();
    switch (field) {
      case "name":
        if (!trimmed) return "Il nome è obbligatorio.";
        if (trimmed.length < 2) return "Il nome deve avere almeno 2 caratteri.";
        return "";
      case "surname":
        if (!trimmed) return "Il cognome è obbligatorio.";
        if (trimmed.length < 2)
          return "Il cognome deve avere almeno 2 caratteri.";
        return "";
      case "email":
        if (!trimmed) return "L'indirizzo email è obbligatorio.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
          return "Inserisci un indirizzo email valido (es. nome@dominio.it).";
        }
        return "";
      case "phone":
        if (trimmed && !/^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]{6,15}$/.test(trimmed)) {
          return "Inserisci un numero di telefono valido (es. +39 333 1234567).";
        }
        return "";
      case "address":
        return "";
      case "city":
        return "";
      case "country":
        if (!trimmed) return "La nazione è obbligatoria.";
        return "";
      default:
        return "";
    }
  };

  const handleBlur = (field: string, val: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const err = validateField(field, val);
    setErrors((prev) => {
      const next = { ...prev };
      if (err) next[field] = err;
      else delete next[field];
      return next;
    });
  };

  const handleChangeField = (
    field: string,
    val: string,
    setter: (v: string) => void,
  ) => {
    setter(val);
    if (touched[field]) {
      const err = validateField(field, val);
      setErrors((prev) => {
        const next = { ...prev };
        if (err) next[field] = err;
        else delete next[field];
        return next;
      });
    }
  };

  const validateAll = (): {
    isValid: boolean;
    newErrors: Record<string, string>;
    missingFields: string[];
  } => {
    const newErrors: Record<string, string> = {};
    const missingFields: string[] = [];

    const checks: Array<{ field: string; label: string; val: string }> = [
      { field: "name", label: "Nome", val: name },
      { field: "surname", label: "Cognome", val: surname },
      { field: "email", label: "Email", val: email },
      { field: "country", label: "Nazione", val: country },
    ];

    checks.forEach(({ field, label, val }) => {
      const err = validateField(field, val);
      if (err) {
        newErrors[field] = err;
        missingFields.push(label);
      }
    });

    // Mark all as touched
    const allTouched: Record<string, boolean> = {};
    checks.forEach((c) => (allTouched[c.field] = true));
    setTouched(allTouched);
    setErrors(newErrors);

    return {
      isValid: Object.keys(newErrors).length === 0,
      newErrors,
      missingFields,
    };
  };

  // Combine variant payment methods (if available), CRM methods and Sysdat channel manager methods into unified options list
  const paymentOptions = useMemo(() => {
    const options: Array<{
      key: string;
      id: number;
      code?: string;
      label: string;
      description: string;
      isSysdat: boolean;
      gatewayId?: string;
      depositValue?: number;
      depositType?: string;
      requiresDeposit?: boolean;
    }> = [];

    // 0. If estimate specifically defines a CRM payment method (not Channel Manager 5), respect that setting
    if (
      estimatePaymentMethodId &&
      estimatePaymentMethodId !== PaymentMethodType.ChannelManager
    ) {
      const depVal = estimateDepositValue ?? selectedVariant?.depositValue ?? 0;
      const depType =
        estimateDepositType || selectedVariant?.depositType || "fixed";
      const hasDep = depVal > 0;

      if (estimatePaymentMethodId === PaymentMethodType.InStructure) {
        options.push({
          key: "crm-1",
          id: PaymentMethodType.InStructure,
          label: "Pagamento in struttura",
          description: hasDep
            ? `Richiesta caparra: ${formatDepositDescription(depVal, depType)}. Saldo al check-in in hotel.`
            : "Conferma la prenotazione senza pagamento immediato e salda in hotel",
          isSysdat: false,
          gatewayId: "pay-on-arrival",
          depositValue: depVal,
          depositType: depType,
          requiresDeposit: hasDep,
        });
        return options;
      }

      if (estimatePaymentMethodId === PaymentMethodType.CreditCard) {
        options.push({
          key: "crm-2",
          id: PaymentMethodType.CreditCard,
          label: "Carta di credito (Gateway)",
          description: hasDep
            ? `Richiesta caparra: ${formatDepositDescription(depVal, depType)}. Pagamento sicuro online con Nexi XPay.`
            : "Pagamento sicuro online tramite circuito Nexi XPay",
          isSysdat: false,
          gatewayId: "nexi",
          depositValue: depVal,
          depositType: depType,
          requiresDeposit: hasDep,
        });
        return options;
      }

      if (estimatePaymentMethodId === PaymentMethodType.BankTransfer) {
        options.push({
          key: "crm-3",
          id: PaymentMethodType.BankTransfer,
          label: "Bonifico Bancario",
          description: hasDep
            ? `Richiesta caparra: ${formatDepositDescription(depVal, depType)} tramite bonifico bancario.`
            : "Invia una caparra/saldo tramite bonifico bancario",
          isSysdat: false,
          gatewayId: "bank-transfer",
          depositValue: depVal,
          depositType: depType,
          requiresDeposit: hasDep,
        });
        return options;
      }

      if (estimatePaymentMethodId === PaymentMethodType.OfflineConfirmation) {
        options.push({
          key: "crm-4",
          id: PaymentMethodType.OfflineConfirmation,
          label: "Conferma senza pagamento",
          description:
            "Conferma la prenotazione. Sarai ricontattato dalla reception.",
          isSysdat: false,
          gatewayId: "pay-on-arrival",
          depositValue: 0,
          depositType: "fixed",
          requiresDeposit: false,
        });
        return options;
      }
    }

    // 1. Variant-specific payment methods derived from cod_pagamenti mapped in CRM
    const variantMethods = selectedVariant?.paymentMethods;
    if (variantMethods && variantMethods.length > 0) {
      variantMethods.forEach((m) => {
        if (!m.isActive) return;
        const typeId = m.type ?? m.id;
        if (typeId === PaymentMethodType.CreditCard) {
          options.push({
            key: `var-${m.id}-cc`,
            id: m.id,
            code: m.isChannelManager ? "CC" : undefined,
            label: m.descrizione || "Carta di credito (Gateway)",
            description: m.depositValue
              ? `Richiesta caparra: ${formatDepositDescription(m.depositValue, m.depositType)}. Pagamento sicuro online con Nexi XPay.`
              : "Pagamento sicuro online tramite circuito Nexi XPay",
            isSysdat: m.isChannelManager,
            gatewayId: "nexi",
            depositValue: m.depositValue,
            depositType: m.depositType,
            requiresDeposit:
              m.requiresDeposit ??
              (m.depositValue != null && m.depositValue > 0),
          });
        } else if (typeId === PaymentMethodType.BankTransfer) {
          options.push({
            key: `var-${m.id}-bt`,
            id: m.id,
            code: m.isChannelManager ? "BT" : undefined,
            label: m.descrizione || "Bonifico Bancario",
            description: m.depositValue
              ? `Richiesta caparra: ${formatDepositDescription(m.depositValue, m.depositType)} tramite bonifico bancario.`
              : "Invia una caparra/saldo tramite bonifico bancario",
            isSysdat: m.isChannelManager,
            gatewayId: "bank-transfer",
            depositValue: m.depositValue,
            depositType: m.depositType,
            requiresDeposit:
              m.requiresDeposit ??
              (m.depositValue != null && m.depositValue > 0),
          });
        } else {
          options.push({
            key: `var-${m.id}-arr`,
            id: m.id,
            code: m.isChannelManager ? "IN" : undefined,
            label: m.descrizione || "Pagamento in struttura",
            description:
              "Conferma la prenotazione e paga al tuo arrivo in hotel",
            isSysdat: m.isChannelManager,
            gatewayId: "pay-on-arrival",
            depositValue: m.depositValue,
            depositType: m.depositType,
            requiresDeposit:
              m.requiresDeposit ??
              (m.depositValue != null && m.depositValue > 0),
          });
        }
      });
    }

    // 2. Fallback to estimate-level allowedMethods / sysdatMethods if variant has no specific methods
    if (options.length === 0) {
      if (allowedMethods.length > 0) {
        allowedMethods.forEach((m) => {
          if (
            m.type === PaymentMethodType.OfflineConfirmation ||
            m.isChannelManager
          ) {
            // will expand below
          } else if (
            m.type === PaymentMethodType.CreditCard ||
            m.id === PaymentMethodType.CreditCard
          ) {
            options.push({
              key: `crm-${m.id}`,
              id: m.id,
              label: m.descrizione || "Carta di credito (Gateway)",
              description: m.depositValue
                ? `Richiesta caparra: ${formatDepositDescription(m.depositValue, m.depositType)}. Pagamento sicuro online con Nexi XPay.`
                : "Pagamento sicuro online tramite circuito Nexi XPay",
              isSysdat: false,
              gatewayId: "nexi",
              depositValue: m.depositValue,
              depositType: m.depositType,
              requiresDeposit:
                m.requiresDeposit ??
                (m.depositValue != null && m.depositValue > 0),
            });
          } else if (m.type === PaymentMethodType.BankTransfer) {
            options.push({
              key: `crm-${m.id}`,
              id: m.id,
              label: m.descrizione || "Bonifico Bancario",
              description: m.depositValue
                ? `Richiesta caparra: ${formatDepositDescription(m.depositValue, m.depositType)} tramite bonifico bancario.`
                : "Invia una caparra/saldo tramite bonifico bancario",
              isSysdat: false,
              gatewayId: "bank-transfer",
              depositValue: m.depositValue,
              depositType: m.depositType,
              requiresDeposit:
                m.requiresDeposit ??
                (m.depositValue != null && m.depositValue > 0),
            });
          } else {
            options.push({
              key: `crm-${m.id}`,
              id: m.id,
              label: m.descrizione || "Paga in struttura",
              description:
                "Conferma la prenotazione e paga al tuo arrivo in hotel",
              isSysdat: false,
              gatewayId: "pay-on-arrival",
              depositValue: m.depositValue,
              depositType: m.depositType,
              requiresDeposit:
                m.requiresDeposit ??
                (m.depositValue != null && m.depositValue > 0),
            });
          }
        });
      }

      if (sysdatMethods.length > 0) {
        sysdatMethods.forEach((sys) => {
          let targetId =
            sys.localPaymentMethodId ??
            (sys.method === "C"
              ? PaymentMethodType.CreditCard
              : sys.method === "B"
                ? PaymentMethodType.BankTransfer
                : PaymentMethodType.InStructure);
          let gatewayId = "confirm-to-structure";
          let defaultLabel =
            sys.localPaymentMethodName ||
            sys.description ||
            `Pagamento via Channel Manager (${sys.code})`;

          if (targetId === PaymentMethodType.CreditCard || sys.method === "C") {
            gatewayId = "nexi";
            targetId = PaymentMethodType.CreditCard;
            defaultLabel =
              sys.localPaymentMethodName || "Carta di credito (Gateway)";
          } else if (
            targetId === PaymentMethodType.BankTransfer ||
            sys.method === "B"
          ) {
            gatewayId = "bank-transfer";
            targetId = PaymentMethodType.BankTransfer;
            defaultLabel = sys.localPaymentMethodName || "Bonifico Bancario";
          } else if (
            targetId === PaymentMethodType.InStructure ||
            sys.method === "N"
          ) {
            gatewayId = "pay-on-arrival";
            targetId = PaymentMethodType.InStructure;
            defaultLabel = sys.localPaymentMethodName || "Paga in struttura";
          }

          const sysDepositType =
            sys.count === "percent"
              ? "percent"
              : sys.count === "nights" || sys.count === "night"
                ? "nights"
                : "fixed";

          options.push({
            key: `sysdat-${sys.code}`,
            id: targetId,
            code: String(sys.code),
            label: defaultLabel,
            description: sys.value
              ? `Importo richiesto: ${formatDepositDescription(sys.value, sysDepositType)}`
              : "Metodo di pagamento abilitato dal Channel Manager",
            isSysdat: true,
            gatewayId,
            depositValue: sys.value,
            depositType: sysDepositType,
            requiresDeposit: !!(sys.value && sys.value > 0),
          });
        });
      }
    }

    // 3. Absolute fallback: Pagamento in struttura
    if (options.length === 0) {
      options.push({
        key: "crm-1",
        id: 1,
        label: "Pagamento in struttura",
        description:
          "Conferma la prenotazione senza pagamento immediato e salda in hotel",
        isSysdat: false,
        gatewayId: "pay-on-arrival",
      });
    }

    return options;
  }, [
    selectedVariant,
    allowedMethods,
    sysdatMethods,
    estimatePaymentMethodId,
    estimateDepositValue,
    estimateDepositType,
  ]);

  useEffect(() => {
    if (
      paymentOptions.length > 0 &&
      !paymentOptions.find((o) => o.key === selectedMethodKey)
    ) {
      setSelectedMethodKey(paymentOptions[0].key);
    }
  }, [paymentOptions, selectedMethodKey]);

  const selectedOption =
    paymentOptions.find((o) => o.key === selectedMethodKey) ||
    paymentOptions[0];

  // Total estimate amount
  const totalAmount = useMemo(() => {
    const roomsTotal = variant
      ? variant.room?.reduce(
          (s, r) => s + (r.rate?.[0]?.board?.[0]?.riep?.totale || 0),
          0,
        ) || 0
      : 0;
    const servicesTotal = variant
      ? variant.services?.reduce(
          (s, svc) => s + (svc.amount || 0) * (svc.quantity || 1),
          0,
        ) || 0
      : 0;
    return Number((roomsTotal + servicesTotal).toFixed(2));
  }, [variant]);

  // Deposit calculation based on selected payment method and variant
  const depositCalculation = useMemo(() => {
    if (
      selectedOption?.requiresDeposit === false ||
      (selectedOption?.depositValue ?? 0) <= 0
    ) {
      if (selectedOption?.gatewayId === "pay-on-arrival") {
        return {
          hasDeposit: false,
          depositAmount: null,
          depositLabel: "",
          depositType: undefined,
          depositValue: undefined,
        };
      }
    }

    const depositVal =
      selectedOption?.depositValue ??
      (variant?.depositValue && variant.depositValue > 0
        ? variant.depositValue
        : undefined);

    const rawDepositType =
      selectedOption?.depositValue != null
        ? selectedOption.depositType
        : variant?.depositValue
          ? variant.depositType || "fixed"
          : undefined;
    const depositType = rawDepositType?.trim().toLowerCase();

    if (depositVal != null && depositVal > 0) {
      if (depositType === "percent") {
        const calculated = Number(
          ((totalAmount * depositVal) / 100).toFixed(2),
        );
        return {
          hasDeposit: true,
          depositAmount: Math.min(totalAmount, calculated),
          depositLabel: `${depositVal}%`,
          depositType: "percent",
          depositValue: depositVal,
        };
      } else if (depositType === "nights" || depositType === "night") {
        const totalNights =
          variant?.checkIn && variant?.checkOut
            ? Math.max(
                1,
                Math.round(
                  (new Date(variant.checkOut).getTime() -
                    new Date(variant.checkIn).getTime()) /
                    (1000 * 60 * 60 * 24),
                ),
              )
            : 1;
        const calculated = Number(
          (
            (totalAmount / totalNights) *
            Math.min(depositVal, totalNights)
          ).toFixed(2),
        );
        return {
          hasDeposit: true,
          depositAmount: Math.min(totalAmount, calculated),
          depositLabel: `${depositVal} ${depositVal === 1 ? "notte" : "notti"}`,
          depositType: "nights",
          depositValue: depositVal,
        };
      } else {
        const fixed = Number(depositVal.toFixed(2));
        return {
          hasDeposit: true,
          depositAmount: Math.min(totalAmount, fixed),
          depositLabel: `${fixed} €`,
          depositType: "fixed",
          depositValue: fixed,
        };
      }
    }

    return {
      hasDeposit: false,
      depositAmount: null,
      depositLabel: "",
      depositType: undefined,
      depositValue: undefined,
    };
  }, [selectedOption, totalAmount, variant]);

  const activeGateway =
    availableGateways.find((g) => g.id === selectedOption?.gatewayId) ||
    confirmToStructureGateway;
  const GatewayForm = activeGateway?.component;

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (isArrivalPassed) {
      toast.error(
        "Non è possibile confermare il preventivo: la data di arrivo è già superata.",
      );
      return;
    }

    // 1. Run comprehensive frontend validation
    const { isValid, newErrors, missingFields } = validateAll();

    if (!isValid) {
      const firstKey = Object.keys(newErrors)[0];
      const element = document.getElementById(firstKey);
      if (element) {
        element.focus();
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      toast.error("Attenzione: dati incompleti o non validi", {
        description: `Completa i seguenti campi per procedere: ${missingFields.join(", ")}`,
        duration: 5000,
      });
      return;
    }

    if (!gatewayReady) {
      toast.warning(
        "Attendi il caricamento completo del gateway di pagamento.",
      );
      return;
    }

    setSubmitting(true);

    try {
      // 2. Confirm estimate in backend
      const confirmRes = await axios.post("/Estimate/confirm", {
        token,
        variantId: variant?.id,
        name: name.trim(),
        surname: surname.trim(),
        email: email.trim(),
        phone: phone.trim(),
        address: address.trim() || null,
        city: city.trim() || null,
        province: province.trim() || null,
        cap: cap.trim() || null,
        country: country.trim() || null,
        notes: notes.trim() || null,
        paymentMethodId: selectedOption?.id ?? 2,
        paymentMethodCode: selectedOption?.code ?? null,
        paymentMethodDescription: selectedOption?.label ?? null,
        totalAmount,
        depositAmount: depositCalculation.hasDeposit
          ? depositCalculation.depositAmount
          : null,
      });

      if (!confirmRes.data?.success) {
        toast.error(confirmRes.data?.message || t("error"));
        setSubmitting(false);
        return;
      }

      // 3. If payment method is Nexi / Credit Card Gateway, build request and redirect
      if (selectedOption?.gatewayId === "nexi") {
        const estId =
          confirmRes.data?.estimateId ||
          (location.state as any)?.estimateId ||
          0;
        const structId =
          confirmRes.data?.structureId ||
          (location.state as any)?.structureId ||
          0;

        const payableAmount =
          depositCalculation.hasDeposit &&
          depositCalculation.depositAmount &&
          depositCalculation.depositAmount > 0
            ? depositCalculation.depositAmount
            : totalAmount;

        const description = depositCalculation.hasDeposit
          ? `Pagamento caparra (${depositCalculation.depositLabel}) preventivo #${estId}`
          : `Pagamento preventivo #${estId}`;

        const nexiRes = await axios.post("/payment/nexi/build-request", {
          estimateId: estId,
          structureId: structId,
          amount: payableAmount,
          currency: "EUR",
          description: description,
          returnUrl: `${window.location.origin}/confirm-success/${token}`,
          cancelUrl: `${window.location.origin}/preventive-detail/${token}`,
        });

        if (
          nexiRes.data?.success &&
          nexiRes.data?.actionUrl &&
          nexiRes.data?.fields
        ) {
          const form = document.createElement("form");
          form.method = "POST";
          form.action = nexiRes.data.actionUrl;

          Object.entries(nexiRes.data.fields as Record<string, string>).forEach(
            ([k, val]) => {
              const input = document.createElement("input");
              input.type = "hidden";
              input.name = k;
              input.value = val;
              form.appendChild(input);
            },
          );

          document.body.appendChild(form);
          form.submit();
          return;
        } else {
          toast.error(
            nexiRes.data?.error ||
              "Errore nella preparazione del pagamento Nexi",
          );
          setSubmitting(false);
          return;
        }
      }

      // 4. For offline / standard payment methods, navigate directly to confirmation success
      toast.success(t("success") || "Prenotazione confermata con successo!");
      navigate(`/confirm-success/${token}`, { replace: true });
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          t("error") ||
          "Si è verificato un errore.",
      );
      setSubmitting(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Container widened from max-w-4xl to max-w-6xl / 7xl for breathing room */}
      <div className="max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-6"
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              navigate(`/preventive-detail/${token}`, { replace: true })
            }
            className="hover:bg-accent/60 -ml-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" aria-hidden="true" />
            Torna al preventivo
          </Button>
        </motion.div>

        <div className="mb-8">
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl font-bold tracking-tight"
          >
            {t("title") || "Checkout & Conferma Prenotazione"}
          </motion.h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Inserisci i tuoi dati anagrafici e seleziona la modalità di
            pagamento per confermare il soggiorno.
          </p>
        </div>

        {isArrivalPassed && (
          <div className="mb-6 rounded-xl border border-destructive/50 bg-destructive/10 p-4 text-destructive flex items-center gap-3 shadow-xs">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <div className="text-sm font-medium">
              Non è possibile confermare il preventivo: la data di arrivo prevista è già superata.
            </div>
          </div>
        )}

        {/* 12-column grid layout for ideal desktop breathing room */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Main Column: Buyer info and Payment selection */}
          <div className="lg:col-span-7 space-y-8">
            {/* Buyer info section */}
            <motion.section
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              aria-labelledby="buyer-info-heading"
            >
              <div className="flex items-center gap-2 mb-4">
                <User className="h-5 w-5 text-primary" aria-hidden="true" />
                <h2
                  id="buyer-info-heading"
                  className="text-xl font-semibold tracking-tight"
                >
                  {t("buyerInfo") || "Informazioni acquirente"}
                </h2>
              </div>

              <Card className="shadow-sm border">
                <CardContent className="p-6 md:p-7 space-y-5">
                  {/* Name & Surname */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label
                        htmlFor="name"
                        className="text-sm font-medium flex items-center"
                      >
                        {t("name") || "Nome"}
                        <span
                          className="text-destructive font-bold ml-1"
                          aria-hidden="true"
                        >
                          *
                        </span>
                      </Label>
                      <Input
                        ref={nameInputRef}
                        id="name"
                        type="text"
                        placeholder="Es. Mario"
                        value={name}
                        onChange={(e) =>
                          handleChangeField("name", e.target.value, setName)
                        }
                        onBlur={(e) => handleBlur("name", e.target.value)}
                        aria-required="true"
                        aria-invalid={!!errors.name}
                        aria-describedby={
                          errors.name ? "name-error" : undefined
                        }
                        className={
                          errors.name
                            ? "border-destructive focus-visible:ring-destructive"
                            : ""
                        }
                        required
                      />
                      {errors.name && (
                        <p
                          id="name-error"
                          role="alert"
                          className="text-xs text-destructive font-medium flex items-center gap-1 mt-1"
                        >
                          <AlertCircle
                            className="h-3.5 w-3.5 shrink-0"
                            aria-hidden="true"
                          />
                          {errors.name}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="surname"
                        className="text-sm font-medium flex items-center"
                      >
                        {t("surname") || "Cognome"}
                        <span
                          className="text-destructive font-bold ml-1"
                          aria-hidden="true"
                        >
                          *
                        </span>
                      </Label>
                      <Input
                        id="surname"
                        type="text"
                        placeholder="Es. Rossi"
                        value={surname}
                        onChange={(e) =>
                          handleChangeField(
                            "surname",
                            e.target.value,
                            setSurname,
                          )
                        }
                        onBlur={(e) => handleBlur("surname", e.target.value)}
                        aria-required="true"
                        aria-invalid={!!errors.surname}
                        aria-describedby={
                          errors.surname ? "surname-error" : undefined
                        }
                        className={
                          errors.surname
                            ? "border-destructive focus-visible:ring-destructive"
                            : ""
                        }
                        required
                      />
                      {errors.surname && (
                        <p
                          id="surname-error"
                          role="alert"
                          className="text-xs text-destructive font-medium flex items-center gap-1 mt-1"
                        >
                          <AlertCircle
                            className="h-3.5 w-3.5 shrink-0"
                            aria-hidden="true"
                          />
                          {errors.surname}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Email & Phone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label
                        htmlFor="email"
                        className="text-sm font-medium flex items-center"
                      >
                        <Mail
                          className="h-3.5 w-3.5 mr-1 text-muted-foreground"
                          aria-hidden="true"
                        />
                        {t("email") || "Email"}
                        <span
                          className="text-destructive font-bold ml-1"
                          aria-hidden="true"
                        >
                          *
                        </span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="mario.rossi@email.it"
                        value={email}
                        onChange={(e) =>
                          handleChangeField("email", e.target.value, setEmail)
                        }
                        onBlur={(e) => handleBlur("email", e.target.value)}
                        aria-required="true"
                        aria-invalid={!!errors.email}
                        aria-describedby={
                          errors.email ? "email-error" : undefined
                        }
                        className={
                          errors.email
                            ? "border-destructive focus-visible:ring-destructive"
                            : ""
                        }
                        required
                      />
                      {errors.email && (
                        <p
                          id="email-error"
                          role="alert"
                          className="text-xs text-destructive font-medium flex items-center gap-1 mt-1"
                        >
                          <AlertCircle
                            className="h-3.5 w-3.5 shrink-0"
                            aria-hidden="true"
                          />
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="phone"
                        className="text-sm font-medium flex items-center"
                      >
                        <Phone
                          className="h-3.5 w-3.5 mr-1 text-muted-foreground"
                          aria-hidden="true"
                        />
                        {t("phone") || "Telefono"}
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+39 333 123 4567"
                        value={phone}
                        onChange={(e) =>
                          handleChangeField("phone", e.target.value, setPhone)
                        }
                        onBlur={(e) => handleBlur("phone", e.target.value)}
                        aria-invalid={!!errors.phone}
                        aria-describedby={
                          errors.phone ? "phone-error" : undefined
                        }
                        className={
                          errors.phone
                            ? "border-destructive focus-visible:ring-destructive"
                            : ""
                        }
                      />
                      {errors.phone && (
                        <p
                          id="phone-error"
                          role="alert"
                          className="text-xs text-destructive font-medium flex items-center gap-1 mt-1"
                        >
                          <AlertCircle
                            className="h-3.5 w-3.5 shrink-0"
                            aria-hidden="true"
                          />
                          {errors.phone}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Address */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="address"
                      className="text-sm font-medium flex items-center"
                    >
                      <MapPin
                        className="h-3.5 w-3.5 mr-1 text-muted-foreground"
                        aria-hidden="true"
                      />
                      {t("address") || "Indirizzo"}
                    </Label>
                    <Input
                      id="address"
                      type="text"
                      placeholder="Via Roma 1"
                      value={address}
                      onChange={(e) =>
                        handleChangeField("address", e.target.value, setAddress)
                      }
                      onBlur={(e) => handleBlur("address", e.target.value)}
                      aria-invalid={!!errors.address}
                      aria-describedby={
                        errors.address ? "address-error" : undefined
                      }
                      className={
                        errors.address
                          ? "border-destructive focus-visible:ring-destructive"
                          : ""
                      }
                    />
                      {errors.address && (
                        <p
                          id="address-error"
                          role="alert"
                          className="text-xs text-destructive font-medium flex items-center gap-1 mt-1"
                        >
                          <AlertCircle
                            className="h-3.5 w-3.5 shrink-0"
                            aria-hidden="true"
                          />
                          {errors.address}
                        </p>
                      )}
                  </div>

                  {/* City (Searchable via GeoController), Province (Only for IT), CAP */}
                  <div className="flex flex-col sm:flex-row gap-4 items-start">
                    <div className="flex-1 w-full space-y-2">
                      <Label
                        htmlFor="city"
                        className="text-sm font-medium flex items-center"
                      >
                        {t("city") || "Città"}
                      </Label>
                      <CitySelect
                        id="city"
                        value={city}
                        countryId={currentCountryObj?.id}
                        onChange={(val) =>
                          handleChangeField("city", val, setCity)
                        }
                        onSelectCity={(cityData) => {
                          setCity(cityData.name);
                          const targetCountry = (
                            cityData.countryIso2 || country
                          ).toUpperCase();
                          if (cityData.countryIso2) {
                            setCountry(targetCountry);
                          }
                          if (targetCountry === "IT") {
                            const matched = ITALIAN_PROVINCES.find(
                              (p) =>
                                p.label.toLowerCase() ===
                                cityData.name.trim().toLowerCase(),
                            );
                            setProvince(matched ? matched.value : "");
                          } else {
                            setProvince("");
                          }
                          // Clear errors for city and country
                          setErrors((prev) => {
                            const next = { ...prev };
                            delete next.city;
                            delete next.country;
                            return next;
                          });
                        }}
                        error={errors.city}
                        placeholder="Cerca o inserisci città..."
                      />
                      {errors.city && (
                        <p
                          id="city-error"
                          role="alert"
                          className="text-xs text-destructive font-medium flex items-center gap-1 mt-1"
                        >
                          <AlertCircle
                            className="h-3.5 w-3.5 shrink-0"
                            aria-hidden="true"
                          />
                          {errors.city}
                        </p>
                      )}
                    </div>

                    {country === "IT" && (
                      <div className="w-full sm:w-56 md:w-60 shrink-0 space-y-2">
                        <Label
                          htmlFor="province-select"
                          className="text-sm font-medium"
                        >
                          {t("province") || "Provincia"}
                        </Label>
                        <ProvinceSelect
                          id="province-select"
                          value={province}
                          onChange={setProvince}
                        />
                      </div>
                    )}

                    <div className="w-full sm:w-28 shrink-0 space-y-2">
                      <Label htmlFor="cap" className="text-sm font-medium">
                        {t("cap") || "CAP"}
                      </Label>
                      <Input
                        id="cap"
                        type="text"
                        placeholder="20100"
                        maxLength={10}
                        value={cap}
                        onChange={(e) => setCap(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Country (Searchable Combobox with Flags from GeoController) */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="country-select"
                      className="text-sm font-medium flex items-center"
                    >
                      {t("country") || "Nazione"}
                      <span
                        className="text-destructive font-bold ml-1"
                        aria-hidden="true"
                      >
                        *
                      </span>
                    </Label>
                    <CountrySelect
                      id="country-select"
                      value={country}
                      countries={countries}
                      isLoading={loadingCountries}
                      onChange={(iso, c) => {
                        setCountry(iso);
                        if (iso !== "IT") {
                          setProvince("");
                        }
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.country;
                          return next;
                        });
                      }}
                      error={errors.country}
                    />
                    {errors.country && (
                      <p
                        id="country-select-error"
                        role="alert"
                        className="text-xs text-destructive font-medium flex items-center gap-1 mt-1"
                      >
                        <AlertCircle
                          className="h-3.5 w-3.5 shrink-0"
                          aria-hidden="true"
                        />
                        {errors.country}
                      </p>
                    )}
                  </div>

                  {/* Special Requests / Notes */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="notes"
                      className="text-sm font-medium flex items-center"
                    >
                      <FileText
                        className="h-3.5 w-3.5 mr-1 text-muted-foreground"
                        aria-hidden="true"
                      />
                      {t("notes") || "Note e richieste speciali"}
                    </Label>
                    <textarea
                      id="notes"
                      placeholder="Eventuali intolleranze alimentari, orario previsto di arrivo, preferenze..."
                      aria-label="Note e richieste speciali per la struttura"
                      className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </div>
                </CardContent>
              </Card>
            </motion.section>

            {/* Payment method section */}
            <motion.section
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              aria-labelledby="payment-methods-heading"
            >
              <div className="flex items-center gap-2 mb-4">
                <CreditCard
                  className="h-5 w-5 text-primary"
                  aria-hidden="true"
                />
                <h2
                  id="payment-methods-heading"
                  className="text-xl font-semibold tracking-tight"
                >
                  {t("paymentMethod") || "Modalità di pagamento"}
                </h2>
              </div>

              {loadingPayments ? (
                <div className="flex items-center justify-center p-8 border rounded-2xl bg-card shadow-xs">
                  <Loader2
                    className="h-5 w-5 animate-spin text-primary mr-3"
                    aria-hidden="true"
                  />
                  <span className="text-sm font-medium text-muted-foreground">
                    Caricamento metodi di pagamento disponibili...
                  </span>
                </div>
              ) : (
                <div
                  className="space-y-3.5"
                  role="radiogroup"
                  aria-label="Metodi di pagamento disponibili"
                >
                  {paymentOptions.map((opt, i) => {
                    const isSelected = selectedMethodKey === opt.key;

                    return (
                      <motion.div
                        key={opt.key}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 * i }}
                      >
                        <div
                          role="radio"
                          aria-checked={isSelected}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setSelectedMethodKey(opt.key);
                            }
                          }}
                          className={`rounded-xl border p-4 sm:p-5 cursor-pointer transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                            isSelected
                              ? "border-primary bg-primary/[0.03] ring-1 ring-primary/20 shadow-xs"
                              : "hover:border-primary/40 hover:bg-muted/20"
                          }`}
                          onClick={() => setSelectedMethodKey(opt.key)}
                        >
                          <div className="flex items-start sm:items-center gap-3.5">
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5 sm:mt-0">
                              {opt.gatewayId === "nexi" ? (
                                <CreditCard
                                  className="h-5 w-5 text-primary"
                                  aria-hidden="true"
                                />
                              ) : opt.isSysdat ? (
                                <ShieldCheck
                                  className="h-5 w-5 text-primary"
                                  aria-hidden="true"
                                />
                              ) : opt.gatewayId === "confirm-to-structure" ? (
                                <Handshake
                                  className="h-5 w-5 text-primary"
                                  aria-hidden="true"
                                />
                              ) : (
                                <Building2
                                  className="h-5 w-5 text-primary"
                                  aria-hidden="true"
                                />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-base leading-snug">
                                {opt.label}
                              </p>
                              <p className="text-sm text-muted-foreground mt-0.5">
                                {opt.description}
                              </p>
                            </div>
                            <div className="shrink-0 flex items-center">
                              <div
                                className={`h-4 w-4 rounded-full border flex items-center justify-center ${
                                  isSelected
                                    ? "border-primary"
                                    : "border-muted-foreground/40"
                                }`}
                              >
                                {isSelected && (
                                  <div className="h-2 w-2 rounded-full bg-primary" />
                                )}
                              </div>
                            </div>
                          </div>

                          <AnimatePresence mode="wait">
                            {isSelected && GatewayForm && (
                              <motion.div
                                key={opt.key}
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                              >
                                <div className="pt-3 border-t mt-3">
                                  <GatewayForm
                                    onReady={setGatewayReady}
                                    text={
                                      opt.gatewayId === "bank-transfer"
                                        ? paymentBankTransferText || undefined
                                        : undefined
                                    }
                                    amount={
                                      depositCalculation.hasDeposit &&
                                      depositCalculation.depositAmount
                                        ? depositCalculation.depositAmount
                                        : totalAmount
                                    }
                                    depositLabel={
                                      depositCalculation.hasDeposit
                                        ? depositCalculation.depositLabel
                                        : undefined
                                    }
                                  />
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </motion.section>

            {/* Confirmation Submit Button */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="pt-2"
            >
              <Button
                type="submit"
                size="lg"
                className="w-full h-12 text-base font-semibold shadow-md cursor-pointer"
                disabled={submitting || isArrivalPassed}
                onClick={handleSubmit}
              >
                {submitting ? (
                  <>
                    <Loader2
                      className="h-5 w-5 mr-2 animate-spin"
                      aria-hidden="true"
                    />
                    {t("confirming") || "Conferma in corso..."}
                  </>
                ) : isArrivalPassed ? (
                  "Data di arrivo superata"
                ) : (
                  t("confirm") || "Conferma e procedi con la prenotazione"
                )}
              </Button>
              <p className="text-xs text-center text-muted-foreground mt-2">
                Procedendo confermi i dati inseriti e accetti i termini, le
                condizioni e l&apos;
                <button
                  type="button"
                  onClick={() => setIsPrivacyDialogOpen(true)}
                  className="underline text-primary hover:text-primary/80 font-medium cursor-pointer"
                >
                  informativa sulla privacy
                </button>{" "}
                della struttura ricettiva.
              </p>
              <PrivacyPolicyDialog
                open={isPrivacyDialogOpen}
                onOpenChange={setIsPrivacyDialogOpen}
                title="Informativa sulla Privacy"
                htmlContent={privacyPolicyHtml}
              />
            </motion.div>
          </div>

          {/* Sidebar Column: Sticky Order Summary */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-8">
              {variant && (
                <OrderSummary
                  variant={variant}
                  adultsNumber={adultsNumber}
                  childrenNumber={childrenNumber}
                  depositAmount={
                    depositCalculation.hasDeposit
                      ? depositCalculation.depositAmount
                      : undefined
                  }
                  depositLabel={
                    depositCalculation.hasDeposit
                      ? depositCalculation.depositLabel
                      : undefined
                  }
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
